import { EloPlayer, EloResult } from './elo';

export abstract class GameSpecifier {
  abstract id: string;
  abstract name: string;
  abstract description: string;
  abstract trackPoints: boolean;
  abstract rankingLabel: string;
  hasRounds?: boolean;
  roundsCount?: number;

  abstract calculateEloChanges(players: EloPlayer[]): EloResult[];
}

export class GeneralGameSpecifier extends GameSpecifier {
  id = 'general';
  name = 'Standard';
  description = 'Standard positional ranking';
  trackPoints = false;
  rankingLabel = 'ELO';
  kFactor = 32;

  calculateEloChanges(players: EloPlayer[]): EloResult[] {
    const n = players.length;
    const results: EloResult[] = players.map(p => ({
      uid: p.uid,
      scoreChange: 0,
      newScore: p.score
    }));

    for (let i = 0; i < n; i++) {
      for (let j = 0; j < n; j++) {
        if (i === j) continue;

        const p1 = players[i];
        const p2 = players[j];

        const expected = 1 / (1 + Math.pow(10, (p2.score - p1.score) / 400));

        let actual = 0.5;
        if (p1.position < p2.position) {
          actual = 1; 
        } else if (p1.position > p2.position) {
          actual = 0; 
        }

        const change = (this.kFactor / (n - 1)) * (actual - expected);
        
        results[i].scoreChange += change;
      }
    }

    return results.map(r => ({
      ...r,
      scoreChange: Math.round(r.scoreChange),
      newScore: Math.round(r.newScore + r.scoreChange)
    }));
  }
}

export class GeneralPointsGameSpecifier extends GeneralGameSpecifier {
  id = 'general_points';
  name = 'Standard with numeric scores';
  description = 'Standard positional ranking with numeric point tracking';
  trackPoints = true;
  rankingLabel = 'POINTS';
}

export class ArschlochGameSpecifier extends GameSpecifier {
  id = 'arschloch';
  name = 'Arschloch';
  description = '4-round game. 25% Elo round 1, +-10% per place change in subsequent rounds, 70% Elo for final placement.';
  trackPoints = false;
  rankingLabel = 'ELO';
  hasRounds = true;
  roundsCount = 4;
  kFactor = 32;

  calculateEloChanges(players: EloPlayer[]): EloResult[] {
    const n = players.length;
    const results: EloResult[] = players.map(p => ({
      uid: p.uid,
      scoreChange: 0,
      newScore: p.score,
      roundEloChanges: Array(this.roundsCount).fill(0)
    }));

    const calculateStandardElo = (positions: number[], k: number) => {
      const changes = new Array(n).fill(0);
      for (let i = 0; i < n; i++) {
        for (let j = 0; j < n; j++) {
          if (i === j) continue;
          const p1 = players[i];
          const p2 = players[j];
          const expected = 1 / (1 + Math.pow(10, (p2.score - p1.score) / 400));
          let actual = 0.5;
          if (positions[i] < positions[j]) {
            actual = 1; 
          } else if (positions[i] > positions[j]) {
            actual = 0; 
          }
          changes[i] += (k / (n - 1)) * (actual - expected);
        }
      }
      return changes;
    };

    // Round 1 (25% Elo)
    const round1Positions = players.map(p => p.positions?.[0] || p.position);
    const round1Changes = calculateStandardElo(round1Positions, this.kFactor * 0.25);
    for (let i = 0; i < n; i++) {
      results[i].scoreChange += round1Changes[i];
      if (results[i].roundEloChanges) results[i].roundEloChanges![0] = Math.round(round1Changes[i]);
    }

    // Subsequent rounds (Round 2 to 4)
    for (let r = 1; r < this.roundsCount; r++) {
      // Normalize so jumping from last to first is always 6 Elo
      // Formula: 6 / (n - 1)
      const eloPerPlace = n > 1 ? 6 / (n - 1) : 0;
      
      for (let i = 0; i < n; i++) {
        const prevPos = players[i].positions?.[r - 1] || players[i].position;
        const currentPos = players[i].positions?.[r] || players[i].position;
        const placeChange = prevPos - currentPos; // Positive means gained places (e.g., 3 to 1 is +2)
        const roundChange = placeChange * eloPerPlace;
        results[i].scoreChange += roundChange;
        if (results[i].roundEloChanges) results[i].roundEloChanges![r] += Math.round(roundChange);
      }
    }

    // Final placement (70% Elo) - added to the final round (Round 4)
    const finalPositions = players.map(p => p.positions?.[this.roundsCount - 1] || p.position);
    const finalChanges = calculateStandardElo(finalPositions, this.kFactor * 0.70);
    for (let i = 0; i < n; i++) {
      results[i].scoreChange += finalChanges[i];
      if (results[i].roundEloChanges) results[i].roundEloChanges![this.roundsCount - 1] += Math.round(finalChanges[i]);
    }

    return results.map(r => ({
      ...r,
      scoreChange: Math.round(r.scoreChange),
      newScore: Math.round(r.newScore + r.scoreChange)
    }));
  }
}

export class PokerGameSpecifier extends GeneralPointsGameSpecifier {
  id = 'poker';
  name = 'Poker';
  description = 'Record poker sessions. Use numeric points for chip balance. Supports live sessions via PokerRoom.';
  rankingLabel = 'POINTS';
}

export const GAME_SPECIFIERS: Record<string, GameSpecifier> = {
  'general': new GeneralGameSpecifier(),
  'general_points': new GeneralPointsGameSpecifier(),
  'arschloch': new ArschlochGameSpecifier(),
  'poker': new PokerGameSpecifier()
};
